import { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw, AlertTriangle, Inbox, Download } from 'lucide-react'
import { api } from '@/services/api'
import type { SempaiAlertItem } from '@/services/ixen_system.service'

const fmtDate = (iso: string | null) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch { return iso }
}

const timeAgo = (iso: string | null): string => {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const m = Math.floor((Date.now() - t) / 60000)
  if (m < 1) return 'adesso'
  if (m < 60) return `${m} min fa`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h fa`
  return `${Math.floor(h / 24)}g fa`
}

const dirCls = (d: string | null) =>
  d === 'BUY' ? 'text-emerald-400' : d === 'SELL' ? 'text-rose-400' : 'text-slate-400'

export const AurouxAlerts = () => {
  const [alerts, setAlerts] = useState<SempaiAlertItem[]>([])
  const [loading, setLoading] = useState(true)
  const [exportingJson, setExportingJson] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAlerts = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const data = await api.get<SempaiAlertItem[]>('/external/auroux-alerts/recent?limit=100')
      setAlerts(Array.isArray(data) ? data : [])
    } catch (e: any) {
      setError(e?.detail || e?.message || 'Errore caricamento alert')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAlerts() }, [fetchAlerts])
  useEffect(() => {
    const t = setInterval(fetchAlerts, 15000)
    return () => clearInterval(t)
  }, [fetchAlerts])

  const exportJson = useCallback(async () => {
    setExportingJson(true); setError(null)
    try {
      const authData = localStorage.getItem('auth-storage')
      let token: string | null = null
      if (authData) {
        try { token = JSON.parse(authData).state?.token ?? null } catch { /* ignore */ }
      }
      const resp = await fetch('/api/v1/external/auroux-alerts/export-json?limit=10000', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!resp.ok) throw new Error(`Export fallito (HTTP ${resp.status})`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19)
      a.download = `auroux_export_${ts}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e?.message || 'Errore export JSON')
    } finally {
      setExportingJson(false)
    }
  }, [])

  const lastAt = alerts.length > 0 ? alerts[0].received_at : null

  const StatCard = ({ label, value, hint }: { label: string; value: number | string; hint?: string }) => (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {hint && <div className="text-[10px] text-muted-foreground/70 mt-0.5">{hint}</div>}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Totale (ultimi 100)" value={alerts.length} />
        <StatCard label="BUY" value={alerts.filter(a => a.direction === 'BUY').length} />
        <StatCard label="SELL" value={alerts.filter(a => a.direction === 'SELL').length} />
        <StatCard label="Ultimo alert" value={lastAt ? timeAgo(lastAt) : '—'} hint={fmtDate(lastAt)} />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-sky-400" />
              Alert Auroux ricevuti
              <span className="text-xs font-normal text-muted-foreground">
                (MacroDroid → bridge, solo raccolta)
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchAlerts} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="sm" onClick={exportJson} disabled={exportingJson} title="Esporta gli alert nello schema JSON unico">
                {exportingJson ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                JSON
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex items-center gap-2 text-rose-400 text-sm py-6 justify-center">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          ) : loading && alerts.length === 0 ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-10">
              Nessun alert Auroux ricevuto.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Orario</th>
                    <th className="py-2 pr-4 font-medium">Direzione</th>
                    <th className="py-2 pr-4 font-medium">TF</th>
                    <th className="py-2 pr-4 font-medium">Entry</th>
                    <th className="py-2 pr-4 font-medium">Testo originale</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(a => {
                    const isBuy = a.direction === 'BUY'
                    return (
                      <tr key={a.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <div className="tabular-nums">{fmtDate(a.received_at)}</div>
                          <div className="text-[10px] text-muted-foreground">{timeAgo(a.received_at)}</div>
                        </td>
                        <td className={`py-2 pr-4 font-bold whitespace-nowrap ${dirCls(a.direction)}`}>
                          {isBuy ? '▲' : '▼'} {a.direction}
                        </td>
                        <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">{a.timeframe || '—'}</td>
                        <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap tabular-nums">
                          {a.entry_price != null ? a.entry_price.toFixed(2) : '—'}
                        </td>
                        <td className="py-2 pr-4 text-muted-foreground max-w-[320px]">
                          <span className="line-clamp-2 break-words" title={a.raw_text ?? undefined}>{a.raw_text || '—'}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
